import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDateTime } from '@/lib/format';
import { Monitor, Smartphone, LogOut, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function KnownDevices() {
    const { logout } = useAuth();
    const [sessions, setSessions] = useState(null);
    const [busy, setBusy] = useState('');
    const load = () => api.get('/auth/sessions').then(r => setSessions(r.data.sessions || [])).catch(() => setSessions([]));
    useEffect(() => { load(); }, []);

    const logoutDevice = async (s) => {
        if (s.current) { logout(); return; }
        setBusy(s.id);
        try { await api.post(`/auth/sessions/${s.id}/logout`); toast.success(`${s.device_name} (${s.browser}) logged out`); load(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Could not log out that device'); }
        finally { setBusy(''); }
    };

    const logoutOthers = async () => {
        setBusy('others');
        try { const { data } = await api.post('/auth/sessions/logout-others'); toast.success(`Logged out ${data.logged_out} other device(s)`); load(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Could not log out other devices'); }
        finally { setBusy(''); }
    };

    const others = (sessions || []).filter(s => !s.current).length;

    return (
        <Card className="p-4 sm:p-5" data-testid="user-known-devices">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                    <div className="font-display font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#1F9D8A]" /> Known devices</div>
                    <p className="text-xs text-muted-foreground mt-0.5">Devices currently signed in to your account. Don't recognise one? Log it out right away and change your password.</p>
                </div>
                {others > 0 && (
                    <Button variant="outline" size="sm" className="text-rose-700 border-rose-200" disabled={busy === 'others'} onClick={logoutOthers} data-testid="user-logout-other-devices">
                        <LogOut className="h-4 w-4 mr-1" /> Log out all other devices
                    </Button>
                )}
            </div>
            {sessions === null && <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>}
            <div className="space-y-2">
                {(sessions || []).map(s => {
                    const Icon = s.device === 'Desktop' ? Monitor : Smartphone;
                    return (
                        <div key={s.id} className="flex items-center gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-secondary/40" data-testid={`user-device-${s.id}`}>
                            <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-[#0E2A47]" /></div>
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                                    <span data-testid={`user-device-name-${s.id}`}>{s.device_name}</span>
                                    {s.current && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="user-device-current-badge">This device</span>}
                                </div>
                                <div className="text-xs text-foreground/70" data-testid={`user-device-browser-${s.id}`}>{s.browser} · {s.os}</div>
                                <div className="text-xs text-muted-foreground">IP <span className="font-mono">{s.last_ip || s.ip}</span> · Signed in {formatDateTime(s.created_at)} · Active {formatDateTime(s.last_seen)}</div>
                            </div>
                            <Button size="sm" variant="outline" disabled={busy === s.id} onClick={() => logoutDevice(s)} className={s.current ? '' : 'text-rose-700 border-rose-200'} data-testid={`user-device-logout-${s.id}`}>
                                <LogOut className="h-3.5 w-3.5 mr-1" /> Logout
                            </Button>
                        </div>
                    );
                })}
                {sessions && sessions.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No tracked devices yet — they appear after your next login.</div>}
            </div>
        </Card>
    );
}
